import { formatDate } from "date-fns";
import { dateformat } from "../../utils/GlobalConstants";
import { AddIcon, ProjectIcon } from "../ui/Icons/TooltipIcon"
import { PlanBuilderContext, Workstream } from "./Plan";
import { WorkstreamProgressChartRow } from "../WorkstreamProgressChart";
import GanttHeader from "./plan_gantt/GanttHeader";
import { useContext } from "react";
import { AppContext } from "@/App";
import { PageLockContext } from "../PageLockProvider";

interface ProjectObjProps {
    name: string;
    onClick: () => void;
    workstreams: Workstream[];
    showChart?: boolean;
    showGantt: boolean;
    projectStartDate: Date;
    projectEndDate: Date;
}

const ProjectObj = ({ name, onClick, workstreams, showChart, showGantt, projectStartDate, projectEndDate }: ProjectObjProps) => {
    const props = useContext(PlanBuilderContext);
    const app = useContext(AppContext);
    const lock = useContext(PageLockContext);

    function getProjectDateRange() {

        if (projectStartDate && projectEndDate) {
            return formatDate(projectStartDate, dateformat) + " - " + formatDate(projectEndDate, dateformat);
        }

        // get earliest start date and latest finish date of all workpackages
        // Flatten all workPackages from all workstreams
        const allWorkPackages = workstreams.flatMap(ws => ws.workPackages);
        let startDate = new Date(allWorkPackages[0].startDate).setHours(0, 0, 0, 0);
        let finishDate = new Date(allWorkPackages[0].finishDate).setHours(0, 0, 0, 0);

        if (allWorkPackages.length > 1) {
            for (let i = 1; i < allWorkPackages.length; i++) {
                if (new Date(allWorkPackages[i].startDate).setHours(0, 0, 0, 0) < startDate) {
                    startDate = new Date(allWorkPackages[i].startDate).setHours(0, 0, 0, 0);
                }
                if (new Date(allWorkPackages[i].finishDate).setHours(0, 0, 0, 0) > finishDate) {
                    finishDate = new Date(allWorkPackages[i].finishDate).setHours(0, 0, 0, 0);
                }
            }
        }

        return formatDate(startDate, dateformat) + " - " + formatDate(finishDate, dateformat);
    }

    function getActualPercentageFromWorkstreams() {
        let actualPercentage = 0;
        workstreams.forEach(workstream => {
            const totalTasks = workstream.workPackages.reduce((total, workPackage) => {
                return total + workPackage.tasks.length;
            }, 0);

            if (totalTasks === 0) return;

            const completedTasks = workstream.workPackages.reduce((total, workPackage) => {
                return total + workPackage.tasks.filter(task => task.completed).length;
            }, 0);

            actualPercentage += (completedTasks / totalTasks) * 100;
        }
        );
        return Math.round(actualPercentage / workstreams.length);
    }

    function getPlannedPercentageFromWorkstreams() {
        let plannedPercentage = 0;
        workstreams.forEach(workstream => {
            const totalTasks = workstream.workPackages.reduce((total, workPackage) => {
                return total + workPackage.tasks.length;
            }, 0);

            if (totalTasks === 0) return;

            const plannedTasks = workstream.workPackages.reduce((total, workPackage) => {
                return total + workPackage.tasks.filter(task => new Date(task.finishDate).setHours(0, 0, 0) <= new Date().setHours(0, 0, 0)).length;
            }, 0);

            plannedPercentage += (plannedTasks / totalTasks) * 100;
        }
        );
        return Math.round(plannedPercentage / workstreams.length);
    }

    // function getActualPercentage() {
    //     const totalTasks = workstreams.reduce((total, workstream) => {
    //         return total + workstream.workPackages.reduce((wpTotal, workPackage) => {
    //             return wpTotal + workPackage.tasks.length;
    //         }, 0);
    //     }, 0);

    //     if (totalTasks === 0) return 0;

    //     const completedTasks = workstreams.reduce((total, workstream) => {
    //         return total + workstream.workPackages.reduce((wpTotal, workPackage) => {
    //             return wpTotal + workPackage.tasks.filter(task => task.completed).length;
    //         }, 0);
    //     }, 0);

    //     return Math.round((completedTasks / totalTasks) * 100);
    // }

    // function getPlannedPercentage() {
    //     const totalTasks = workstreams.reduce((total, workstream) => {
    //         return total + workstream.workPackages.reduce((wpTotal, workPackage) => {
    //             return wpTotal + workPackage.tasks.length;
    //         }, 0);
    //     }, 0);

    //     if (totalTasks === 0) return 0;

    //     const plannedTasks = workstreams.reduce((total, workstream) => {
    //         return total + workstream.workPackages.reduce((wpTotal, workPackage) => {
    //             return wpTotal + workPackage.tasks.filter(task => new Date(task.finishDate).setHours(0, 0, 0) <= new Date().setHours(0, 0, 0)).length;
    //         }, 0);
    //     }, 0);

    //     return Math.round((plannedTasks / totalTasks) * 100);
    // }

    return (
        <div className="flex flex-col">
            <div id={"p"} className={"flex justify-between text-planbuilder-workstream-header-foreground bg-planbuilder-project-header" + (showGantt ? " " : " pr-10 ") + "hover: cursor-pointer"} onClick={() => onClick()}>
                <div className={"flex items-center " + (showGantt ? "min-w-[501px]" : "min-w-[481px]")}>
                    <h2 className="flex items-center truncate overflow-hidden text-ellipsis" ><ProjectIcon className="m-2" />{name}</h2>
                </div>{showChart &&
                    <div className="flex flex-col min-w-[300px] w-full">
                        <WorkstreamProgressChartRow planned={getPlannedPercentageFromWorkstreams()} actual={getActualPercentageFromWorkstreams()} actualColor='bg-green-600' plannedColor='bg-green-400' />
                    </div>}
                {showGantt ?
                    <GanttHeader />
                    :
                    <div className='flex items-center'>
                        <div className="flex flex-col w-[190px] text-right mr-[10px]">{getProjectDateRange()}</div>
                        {app.authLevel >= 4 && !lock.viewOnly ? (
                            <button onClick={(e: React.MouseEvent) => { e.stopPropagation(); props.addWorkPackage(); }} className="p-0 bg-transparent border-0">
                                <AddIcon className="m-2" tooltip="Add Work Package" />
                            </button>
                        ) : <div className="m-2 w-[25px]" />}
                    </div>}
            </div>

            {/*<div className={(open ? "" : " hidden")}>*/}
            {/*    {workstream.workPackages.map((workpackage) => (*/}
            {/*        <WorkPackageObj key={"wp" + workpackage.id} workPackage={workpackage} selected={props.selectedWorkpackageID === workpackage.id} open={props.openWorkpackages?.includes(workpackage.id)} />*/}
            {/*    ))}*/}
            {/*</div>*/}
        </div>
    )
}

export default ProjectObj